import React, { useRef, useLayoutEffect, useEffect, MutableRefObject } from 'react';

import CodeMirror from 'codemirror';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/search/searchcursor';
import './modes/goformz-formula/codemirror-language';
import './modes/workflow/codemirror-workflow';
import './CodeMirrorEditor.scss';
import { CodeMirrorEditorRef } from './codemirror';

export interface CodeMirrorEditorProps {
  mode?: 'goformz-formula' | 'goformz-workflow';
  value?: string;
  onChange?: AnyFunc;
  onCursorPositionChanged?: AnyFunc;
  onEnter?: AnyFunc;
  onFocusChange?: AnyFunc;
  onArrowUpDown?: AnyFunc;
  readOnly?: boolean;
  placeholder?: TypeMePlease;
  singleLine?: boolean;
  validateTokens: AnyFunc;
  autoFocus?: boolean;
  validationDependencies?: React.DependencyList;
}

const CodeMirrorEditor = React.forwardRef<TypeMePlease, CodeMirrorEditorProps>((props, editorRef) => {
  const codeMirrorWrapperRef = useRef<TypeMePlease>(null);
  const textareaNode = useRef<TypeMePlease>();
  const codeMirrorRef = useRef<TypeMePlease>();
  const onEnter = useRef<TypeMePlease>();
  const tabMapping = useRef<TypeMePlease>();
  const keyMapping = useRef<TypeMePlease>();
  const onFocusChange = useRef<TypeMePlease>();
  const onArrowUpDown = useRef<TypeMePlease>();
  const subscribers = useRef<TypeMePlease>();
  const beforeChange = useRef<TypeMePlease>();
  const placeholderRef = useRef<TypeMePlease>();

  (editorRef as MutableRefObject<CodeMirrorEditorRef>).current = {
    focus: () => {
      codeMirrorRef.current.focus();
    },
    focusEnd: () => {
      setTimeout(() => {
        codeMirrorRef.current.focus();
        codeMirrorRef.current.doc.setCursor({
          line: codeMirrorRef.current.doc.lastLine(),
          ch: codeMirrorRef.current.doc.getLine(codeMirrorRef.current.doc.lastLine()).length
        });
      }, 0);
    },
    setCursor: (cursor) => {
      setTimeout(() => {
        codeMirrorRef.current.focus();
        codeMirrorRef.current.doc.setCursor(cursor);
      }, 0);
    },
    getCodeMirrorReference: () => {
      return codeMirrorRef.current;
    },
    on: (eventName, func) => {
      if (!subscribers.current) {
        subscribers.current = {};
      }
      if (!subscribers.current[eventName]) {
        subscribers.current[eventName] = [];
      }
      subscribers.current[eventName].push(func);
    },
    un: (eventName, func) => {
      if (!subscribers.current || !subscribers.current[eventName]) {
        return;
      }
      const funcIndex = subscribers.current[eventName].indexOf(func);
      if (funcIndex > -1) {
        subscribers.current[eventName].splice(funcIndex);
      }
    },
    setOnTab: (func) => {
      codeMirrorRef.current.removeKeyMap(tabMapping.current);
      tabMapping.current = {
        Tab: func
          ? (cm) => {
              cm.replaceSelection('');
              return func();
            }
          : false
      };
      codeMirrorRef.current.addKeyMap(tabMapping.current);
    },
    hasOnTab: () => tabMapping.current.Tab !== false
  };

  onEnter.current = () => {
    if (subscribers.current && subscribers.current.onEnter) {
      for (const func of subscribers.current.onEnter) {
        const r = func();
        if (r === false) {
          return false;
        }
      }
    }
    if (props.onEnter) {
      props.onEnter();
    }
    return true;
  };

  onFocusChange.current = (focused) => {
    if (subscribers.current && subscribers.current.onFocusChange) {
      for (const func of subscribers.current.onFocusChange) {
        func(focused);
      }
    }
    if (props.onFocusChange) {
      props.onFocusChange(focused);
    }
  };

  onArrowUpDown.current = (direction) => {
    if (subscribers.current && subscribers.current.onArrowUpDown) {
      for (const func of subscribers.current.onArrowUpDown) {
        const r = func(direction);
        if (r === false) {
          return false;
        }
      }
    }
    if (props.onArrowUpDown) {
      return props.onArrowUpDown(direction);
    }
    return true;
  };

  beforeChange.current = (doc, change) => {
    if (change.update && change.text.length > 0) {
      const newText = [];

      const lines: string[] = props.singleLine ? [change.text.join(' ')] : change.text;

      for (let i = 0; i < lines.length; i++) {
        let line = lines[i];

        if (line === null || line === undefined || props.readOnly) {
          line = '';
        }

        // replace irregular characters
        line = line.replace(/“/g, '"');
        line = line.replace(/”/g, '"');
        line = line.replace(/–/g, '-');

        newText.push(line);
      }

      change.update(change.from, change.to, newText);
    }
  };

  useLayoutEffect(() => {
    let cancelled = false;
    if (!cancelled && !codeMirrorRef.current) {
      const codemirrorValueChanged = (doc, change) => {
        if (props.onChange && change.origin !== 'setValue') {
          props.onChange(doc.getValue());
        }
      };

      const cursorActivity = (cm) => {
        if (!cancelled) {
          const cursor = cm.doc.getCursor();
          const token = cm.getTokenAt(cursor);

          if (props.onCursorPositionChanged) {
            props.onCursorPositionChanged(cm, cursor, token);
          }

          if (subscribers.current && subscribers.current.cursorPositionChanged) {
            for (const func of subscribers.current.cursorPositionChanged) {
              func(cm, cursor, token);
            }
          }
        }
      };

      codeMirrorRef.current = CodeMirror.fromTextArea(textareaNode.current, {
        lineNumbers: false,
        matchBrackets: true,
        autoCloseBrackets: {
          pairs: getBracketsForMode(props.mode),
          explode: false
        },
        mode: props.mode,
        lineWrapping: true,
        autofocus: false,
        spellcheck: false,
        autocorrect: false,
        autocapitalize: false,
        viewportMargin: Infinity,
        readOnly: props.readOnly
      });

      codeMirrorRef.current.on('change', codemirrorValueChanged);
      codeMirrorRef.current.on('beforeChange', (doc, change) => {
        if (!cancelled) {
          beforeChange.current(doc, change);
        }
      });
      codeMirrorRef.current.on('cursorActivity', cursorActivity);
      codeMirrorRef.current.on('focus', () => onFocusChange.current(true));
      codeMirrorRef.current.on('blur', () => onFocusChange.current(false));

      codeMirrorRef.current.setOption('extraKeys', {
        'Shift-Tab': false,
        'Cmd-G': findAndSelect,
        'Ctrl-G': findAndSelect
      });

      tabMapping.current = { Tab: false };
      codeMirrorRef.current.addKeyMap(tabMapping.current);

      (editorRef as MutableRefObject<TypeMePlease>).current.cm = codeMirrorRef.current;

      if (props.autoFocus) {
        const previousScroll = window.scrollY;
        codeMirrorRef.current.focus();
        codeMirrorRef.current.doc.setCursor({ line: 0, ch: (props.value || '').length });
        window.scrollTo(window.scrollX, previousScroll);
      }
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (codeMirrorRef.current) {
      codeMirrorRef.current.setOption('mode', props.mode);
      codeMirrorRef.current.setOption('autoCloseBrackets', {
        pairs: getBracketsForMode(props.mode),
        explode: false
      });
    }
  }, [props.mode, codeMirrorRef.current]);

  useEffect(() => {
    codeMirrorRef.current.removeKeyMap(keyMapping.current);

    if (props.singleLine) {
      keyMapping.current = {
        Enter: (cm) => {
          cm.replaceSelection('');
          onEnter.current();
        },
        Up: (cm) => {
          if (onArrowUpDown.current('up')) {
            cm.execCommand('goLineUp');
          }
        },
        Down: (cm) => {
          if (onArrowUpDown.current('down')) {
            cm.execCommand('goLineDown');
          }
        }
      };
    } else {
      keyMapping.current = {};
    }

    codeMirrorRef.current.addKeyMap(keyMapping.current);
  }, [props.singleLine]);

  useEffect(() => {
    if (codeMirrorRef.current && props.value !== codeMirrorRef.current.doc.getValue()) {
      codeMirrorRef.current.setValue(props.value || '');
      if (props.autoFocus) {
        codeMirrorRef.current.focus();
        codeMirrorRef.current.doc.setCursor({ line: 0, ch: (props.value || '').length });
      }
    }
  }, [codeMirrorRef.current, props.value]);

  useEffect(() => {
    if (codeMirrorRef.current && props.validateTokens) {
      const validationResult = props.validateTokens(codeMirrorRef.current.getLineTokens(0), props.mode, props.value);

      for (const mark of codeMirrorRef.current.doc.getAllMarks()) {
        if (mark.className === 'validation-error' || mark.className === 'validation-warning') {
          mark.clear();
        }
      }

      if (validationResult && validationResult.errors) {
        for (const error of validationResult.errors) {
          if (error && error.position !== null && error.position !== undefined) {
            const formulaLength = error.formula ? Math.min(error.formula.length, props.value.length - error.position) : 1;
            codeMirrorRef.current.doc.markText(
              { line: 0, ch: error.position },
              { line: 0, ch: error.position + formulaLength },
              { className: error.level === 'warning' ? 'validation-warning' : 'validation-error' }
            );
          }
        }
      }
    }
  }, [codeMirrorRef.current, props.value, ...(props.validationDependencies ?? [])]);

  useEffect(() => {
    if (codeMirrorRef.current) {
      codeMirrorRef.current.refresh();
    }
  });

  return (
    <div
      className="codemirror-editor"
      ref={codeMirrorWrapperRef}
      onClick={(e) => {
        if (e.target === codeMirrorWrapperRef.current) {
          (editorRef as MutableRefObject<TypeMePlease>).current.focusEnd();
        }
      }}
    >
      <textarea ref={textareaNode} defaultValue={props.value} />
      {props.value || props.readOnly ? null : (
        <div
          ref={placeholderRef}
          className="placeholder"
          onClick={(e) => {
            if (e.target === placeholderRef.current) {
              (editorRef as MutableRefObject<TypeMePlease>).current.focusEnd();
            }
          }}
        >
          {props.placeholder}
        </div>
      )}
    </div>
  );
});

export default CodeMirrorEditor;

function findAndSelect(cm) {
  const query = cm.getSelection();
  if (query) {
    const selections = [];
    for (const cursor = cm.getSearchCursor(query, 0, { multiline: false }); cursor.findNext(); ) {
      selections.push({ anchor: cursor.pos.from, head: cursor.pos.to });
    }

    cm.setSelections(selections);
  }
}

function getBracketsForMode(mode) {
  switch (mode) {
    case 'goformz-workflow':
      return '{}';
    case 'goformz-formula':
    default:
      return '()[]\'\'""';
  }
}
